import { TipoDomandaDTO } from '@framework/api-client';
import { assertNever } from '@framework/engine';
import { useTranslation } from '@framework/i18n';
import { Tag } from 'antd';
import { useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { isInquiryInTesting } from './isInquiryInTesting';

const StyledTag = styled(Tag)<{ $mini?: boolean }>`
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: ${props => (props.$mini ? '14px' : '20px')};
  padding-inline: ${props => (props.$mini ? '4px' : '7px')};
  &&& {
    font-weight: 600;
  }
`;

export interface InquiryTypeStateTagProps {
  inquiry: TipoDomandaDTO;
  mini?: boolean;
}

/**
 * Stato di un Tipo Domanda
 */
export function InquiryTypeStateTag(props: InquiryTypeStateTagProps) {
  const { inquiry, mini } = props;
  const inquiryTag = useInquiryTag(inquiry);

  if (!inquiryTag) return null;

  return <StyledTag $mini={mini} {...inquiryTag} />;
}

/**
 * Ritorna le impostazioni per il tag
 *
 */
function useInquiryTag(inquiry: TipoDomandaDTO) {
  const theme = useTheme();
  const { t } = useTranslation();

  const state = inquiry.stato;

  const tag = useMemo(() => {
    if (!state) return;

    // Caso speciale: Collaudo
    if (
      isInquiryInTesting(inquiry) ||
      state === 'COLLAUDO' /* necessario per type assertion */
    ) {
      return {
        color: '#9254de',
        children: t('stato.tipo-domanda.collaudo', 'COLLAUDO'),
      };
    }

    // Caso speciale: Anteprima
    if (state === 'APERTA' && inquiry.flBloccaCompilazioneInvio) {
      return {
        color: '#327EA4',
        children: t('stato.tipo-domanda.sola-lettura', 'SOLA LETTURA'),
      };
    }

    switch (state) {
      case 'BOZZA':
        return {
          color: '#757575',
          children: t('stato.tipo-domanda.bozza', 'BOZZA'),
        };
      case 'PRONTA':
        return {
          color: '#05263e',
          children: t('stato.tipo-domanda.pronta', 'PRONTA'),
        };
      case 'PUBBLICATA':
        return {
          color: theme?.colorInfo ?? '#569bc4',
          children: t('stato.tipo-domanda.pubblicata', 'PUBBLICATA'),
        };
      case 'CHIUSA':
        return {
          color: theme?.colorError ?? '#cf1322',
          children: t('stato.tipo-domanda.chiusa', 'CHIUSA'),
        };
      case 'APERTA':
        return {
          color: theme?.colorSuccess ?? '#389e0d',
          children: t('stato.tipo-domanda.aperta', 'APERTA'),
        };
      case 'IN_CORSO':
        return {
          color: '#13adcf',
          children: t('stato.tipo-domanda.in-corso', 'IN CORSO'),
        };
      case 'SOSPESA':
        return {
          color: '#4c6d82',
          children: t('stato.tipo-domanda.sospesa', 'SOSPESA'),
        };
      case 'ANNULLATA':
        return {
          color: theme?.colorWarning ?? '#faad14',
          children: t('stato.tipo-domanda.annullata', 'ANNULLATA'),
        };
    }
    assertNever(state);
  }, [
    inquiry.dataUltimaMessaInCollaudo,
    inquiry.flBloccaCompilazioneInvio,
    state,
    t,
    theme?.colorError,
    theme?.colorInfo,
    theme?.colorSuccess,
    theme?.colorWarning,
  ]);

  return tag;
}
